<template>
  <vf-dialog-layout class-wrapper="!p-0">
    <template #title>
      {{ $t.quickshop }}
    </template>
    <section v-if="!closeOnAdd" data-scroll-el="notificationSection">
      <vf-notification
        v-if="cart.notification.message"
        class="m-2 mt-3 md:mb-4 md:mt-2"
        :type="cart.notification.type"
        @close="cart.notification.message = ''"
      >
        {{ cart.notification.message }}
      </vf-notification>
    </section>
    <section v-if="details && url" class="relative px-4 pt-4">
      <div v-if="variant === 'detailed'" class="flex gap-3">
        <base-picture
          v-if="quickshopImage"
          alt=""
          class="shrink-0"
          height="85"
          :src="quickshopImage.src"
          width="85"
        />
        <div class="min-w-0 flex-1">
          <span class="line-clamp-2 block subtitle-5">
            {{ details.name }}
          </span>
          <span class="body-4 underlined i-block">
            {{ $t.viewDetails }}
          </span>
          <product-pricing
            class="mt-3"
            :currency="details.currency"
            :price="details.price"
            :show-discount-percentage="$feature.showDiscountPercentageOnCatalog"
          />
        </div>
      </div>
      <div v-else>
        <span class="line-clamp-2 block subtitle-5">
          {{ details.name }}
        </span>
        <span class="body-4 underlined i-block">
          {{ $t.viewDetails }}
        </span>
        <product-pricing
          class="mt-3"
          :currency="details.currency"
          :price="details.price"
          :show-discount-percentage="$feature.showDiscountPercentageOnCatalog"
        />
      </div>
      <base-link :aria-label="details.name" class="absolute-0" :to="url" />
    </section>
    <div v-if="!inventory">
      <div class="mb-6 mt-4 px-4">
        <div class="mb-2 h-4 w-30 skeleton" />
        <div class="my-2 flex">
          <div v-for="key in 5" :key class="m-2 h-6 w-6 skeleton" />
        </div>
      </div>
      <div v-for="key in 10" :key class="h-12 b-b b-white skeleton" />
    </div>
    <div v-else class="mb-20 space-y-6">
      <section
        v-for="({ options, label, type }) in displayedAttributes"
        :key="type"
        class="relative"
        :data-scroll-el="`size-picker-${type}`"
      >
        <fieldset>
          <legend class="w-full b-b b-grey-70 p-4 subtitle-4 fw-bold" :class="{ 'sr-only': type === 'color' }">
            {{ $t.selectAttribute[type === 'size' ? isDigitalProduct ? 'amount' : 'size' : type] }}
          </legend>
          <div v-if="type === 'color'" class="px-4">
            <template v-if="variant === 'detailed'">
              <p class="mt-8">
                <span class="subtitle-4">{{ label }}:</span>
                <span class="ml-1 body-3">{{ attributeSelectionLabels[type]?.value }}</span>
              </p>
              <div class="flex wrap -mx-2">
                <vf-color-picker
                  v-for="({ available, id, label: optionLabel, price, swatch }) in options"
                  :key="optionLabel"
                  :active="details?.id === id"
                  :color="swatch?.color"
                  :loading="getIsLoading(id)"
                  :name="optionLabel"
                  :sale="$feature.showOnSaleSwatchIndicatorOnPDP && price.lowCurrent < price.lowOriginal"
                  :size="swatches.size"
                  :thumbnail="swatch?.image"
                  :unavailable="!getIsLoading(id) && !isComingSoon && !available"
                  :variant="swatches.variant"
                  @click="selectNewColor(id, optionLabel)"
                />
              </div>
            </template>
            <p v-else class="py-3 body-3">
              <span class="subtitle-4 fw-bold">{{ label }}</span>: {{ attributeSelectionLabels[type]?.value }}
            </p>
          </div>
          <template v-else>
            <base-radio
              v-for="({ available, dynamicPrice, label: sizeLabel, stockPrice, stockStatus, value }, key)
                in options.map(getVariantOptions)"
              :key
              data-test-id="vf-size-picker"
              :model-value="attributeSelection[type]"
              :name="`${details?.id || product?.id}-${type}`"
              :value
              @pointerdown="enableScroll = true"
              @update:model-value="selectSize(value, sizeLabel, type)"
            >
              <div v-if="productPending || inventoryPending" class="h-12 b-b b-white skeleton" />
              <span
                v-else
                class="h-12 flex cursor-pointer items-center b-b b-grey-80 px-4 between peer-focus-visible:outline-auto"
                :class="{
                  'bg-grey-90 c-grey-20': attributeSelection[type] !== value
                    && (!available || !getAttrOptionAvailable(type, value)),
                  'bg-brand-4 c-white': attributeSelection[type] === value,
                }"
              >
                <span>{{ sizeLabel }}</span>
                <span v-if="available && getAttrOptionAvailable(type, value)" class="text-xs capitalize">
                  <prime-badge
                    v-if="$feature.showBuyWithPrimeOnQuickshop && type === 'size'"
                    :product-id
                    :product-sku="getMatchingVariant(type, value)?.id"
                    type="deliveryMessage"
                  />
                  <span v-if="stockStatus">{{ stockStatus }}</span>
                  <span v-if="stockPrice" class="ml-4 fw-bold" :class="{ 'c-brand-3': dynamicPrice }">
                    {{ stockPrice }}
                  </span>
                </span>
                <template v-else>
                  <span
                    v-if="$feature.showNotifyMeOnQuickshop && getAttrOptionNotifyMe(type, value)"
                    class="text-xs"
                  >
                    {{ $t.notifyMe }}
                  </span>
                  <span v-else class="text-xs">{{ $t.outOfStock }}</span>
                </template>
              </span>
            </base-radio>
          </template>
        </fieldset>
      </section>
    </div>
    <template v-if="selectedVariant && !(productPending || inventoryPending)" #footer>
      <product-cta
        :add-to-cart-label="context === 'bundle-selection' ? $t.save : undefined"
        :attribute-selection
        class="w-full"
        :include-coming-soon="$feature.showComingSoonOnQuickshop"
        :include-notify-me="$feature.showNotifyMeOnQuickshop"
        :include-update-in-cart="context === 'update-cart-item' || context === 'update-saved-item'"
        :loading
        :product="details"
        @add-to-cart="handleAddToCart"
        @notify-me="handleNotifyMe"
        @update-in-cart="updateInCart"
      />
      <cart-express-checkout-ctas
        v-if="isProductAvailable"
        class-apple-pay="mb-4 h-12 w-full"
        class-container="-mb-2"
        context="quickshop"
        hide-pay-later-text
        :match-paypal-height="false"
        :selected-variant
      />
    </template>
    <panel-notify-me size="sm" />
  </vf-dialog-layout>
</template>

<script lang="ts" setup>
import type { Emits as DialogQuickshopEmits, Props as DialogQuickshopProps } from '#commerce/components/dialog/quickshop/Quickshop.vue'
import type { ProductVariants } from '#root/api/clients/product/data-contracts'
import type { ShippingMethodContext } from '#types/gtm'
import type { BaseOption, ProductUpsell, VariantWithStockStatus } from '#types/product'
import { ApiErrorCode } from '#root/enums/api'

export type Props = DialogQuickshopProps
export type Emits = DialogQuickshopEmits

const {
  closeOnAdd,
  context,
  gtmConfig,
  initialAttributes,
  product,
  storeId,
  suppressMiniCart,
  variant
} = defineProps<Props>()
const emit = defineEmits<Emits>()

const productId = defineModel<string>('productId')
const { swatches } = useAppConfig().pages.pdp
const auth = useAuthStore()
const cart = useCartStore()
const { getIsComingSoonProduct } = useComingSoon()
const { DialogSignIn } = useDialogsStore()
const { $feature, $formatPrice, $gtm, $t } = useNuxtApp()
const { trackEvent } = useOptimizely()
const { setProductDetailViewTracking, updateSavedItemInCart, updateItemInCart } = useQuickshop()
const gtmTrace = useSessionStorage('gtmTrace', {} as Record<string, ShippingMethodContext>)
const toast = useToaster()

const PanelNotifyMe = createDialog('notify-me')

const activeProduct = ref(productId.value || product?.id || '')
const enableScroll = ref<boolean>(false)
const loading = ref(false)
const parentInteractionOrigin = ref(getInteractionOrigin())

const {
  productPending,
  productError,
  product: details,
  inventory,
  inventoryPending
} = await useProduct(activeProduct, {
  server: false,
  // @ts-expect-error incorrect Nuxt types
  default: () => product,
  transform: (input) => ({
    ...input,
    badge: input.badge || product?.badge,
    eyebrow: input.eyebrow || product?.eyebrow
  }),
  key: `quickshop-${activeProduct.value}`,
  onResponse: async ({ response: { _data } }) => productId.value = _data.id
})

const displayedAttributes = computed(() =>
  details.value?.attributes
    .filter(({ type, options }) => type !== 'color' || options?.length > 1)
    .sort((attribute) => attribute.type === 'color' ? -1 : 0) || []
)

const { activeGallery } = useActiveGallery(details)
const quickshopImage = computed(() => activeGallery.value.find(({ type }) => type === 'image'))

const attributeSelection = reactive(initialAttributes || {}) as unknown as ProductVariants[number]['attributes']
const attributeSelectionLabels = useAttributeSelectionLabels(details, attributeSelection)
const {
  getAttrOptionAvailable,
  getAttrOptionNotifyMe,
  getMatchingVariant
} = getProductAvailability(attributeSelection, details)

const isAttributeSelectionRequired = computed(
  () => Object.keys(attributeSelection).length >= 1
)
const isVariantSelected = computed(() =>
  isAttributeSelectionRequired.value
  && details.value?.attributes.every(({ type }) => attributeSelection[type] || type === 'color')
)
const selectedVariant = computed(() => {
  const variant = details.value?.variants.find((item) =>
    Object.entries(attributeSelection).every(([key, value]) => !item.attributes[key] || item.attributes[key] === value || key === 'color')
  )
  return variant && isVariantSelected.value ? variant : undefined
})
const nextAttribute = computed(() => {
  const index = Object.entries(attributeSelectionLabels.value).findIndex(
    ([type, { value }]) => type !== 'color' && !value
  )

  return details.value?.attributes[index]
})

const isDigitalProduct = computed(() => details.value?.productType?.toLowerCase() === 'digital')
const isNotifyMe = computed(() => $feature.showNotifyMeOnQuickshop && selectedVariant.value?.notifyMe)
const isOutOfStock = computed(() => selectedVariant.value?.productInventoryState !== 'InStock')
const isSignInToBuy = computed(() => details.value?.presale && !auth.loggedIn)
const isComingSoon = computed(() => $feature.showComingSoonOnQuickshop
  && getIsComingSoonProduct(details.value?.badge, details.value?.eyebrow))
const isProductAvailable = computed(() =>
  !isSignInToBuy.value && !isOutOfStock.value && !isComingSoon.value && !(isNotifyMe.value && isOutOfStock.value)
)
const url = computed(() => details.value?.url || product?.url)

const getIsLoading = (id: string): boolean =>
  activeProduct.value === id && (productPending.value || inventoryPending.value)

const getVariantOptions = ({ available, label, value }: BaseOption) => {
  if (!details.value) return
  const variant = details.value?.variants.find((variant) =>
    variant.attributes.size === value) as VariantWithStockStatus
  let stockStatus = ''
  let stockPrice = ''
  let dynamicPrice = false

  if (variant?.stockStatusLabel && $feature.showSizeLevelStockMessagingOnPDP)
    stockStatus = variant.stockStatusLabel.toLowerCase()

  if ($feature.showDynamicPricing) {
    const { current, historicBestPrice, percentagePriceDiscount, original } = variant.price
    stockPrice = !current ? $t.free : $formatPrice(current, details.value.currency).toString()
    dynamicPrice = !!((percentagePriceDiscount || current !== original) && !historicBestPrice)
  }

  return {
    available,
    dynamicPrice,
    label,
    stockPrice,
    stockStatus,
    value
  }
}

async function addToCart() {
  if (!details.value) return
  const productId = selectedVariant.value?.id
  loading.value = true
  cart.notification.message = ''

  if (!productId) {
    loading.value = false
    return
  }

  setGtmCartProductsMap(productId, details.value)
  if (gtmConfig?.category) setGtmCartProductVariant(productId, { category: gtmConfig.category })

  try {
    const { shippingMethod, shippingStoreId, shippingWindow } = gtmConfig || {}

    if (shippingMethod && shippingStoreId && shippingWindow)
      gtmTrace.value[productId] = { shippingMethod, shippingStoreId, shippingWindow }

    const activeImage = activeGallery.value.find(({ type }) => type === 'image')

    await withRetryAfterTime(() => cart.add({
      productId,
      upc: selectedVariant.value?.upc,
      maxQty: 1,
      productImageURL: activeImage?.thumbnail ?? activeImage?.src,
    }, {
      suppressMiniCart,
      suppressNotification: parentInteractionOrigin.value === 'shop-the-look',
      storeId: shippingStoreId,
      extendedGtmConfig: {
        ...gtmConfig,
        category: gtmConfig?.category || interactionOriginMap[gtmConfig?.appSource!]
      },
    }), 2000, [ApiErrorCode.GWP_PRODUCT])
    trackEvent('quick_shop', {
      product: details.value?.id || activeProduct.value,
      appSource: interactionOriginMap[gtmConfig?.appSource!]
    })
  }
  catch (err) {
    log.error(err)
    delete gtmTrace.value[productId]
  }

  loading.value = false

  if (cart.errorMessage) {
    cart.setNotification('error', cart.errorMessage)
    removeGtmCartProductsMap(productId, details.value.id)
    return
  }

  if ($feature.showMiniCartOnAddProduct || closeOnAdd)
    emit('resolve')
}

// 'bundle-selection' context: don't add to cart here, just hand the picked variant back to the
// caller (usePdp().openQuickshop) - the actual cart.add() happens once, batched, from
// the PDP's own Add to Cart button.
const saveBundleSelection = () => {
  if (!selectedVariant.value || !product) return

  emit('resolve', {
    attributes: selectedVariant.value.attributes,
    attributeLabels: attributeSelectionLabels.value,
    product: {
      ...(product as unknown as ProductUpsell),
      image: quickshopImage.value?.src ?? (product as unknown as ProductUpsell).image
    },
    productId: selectedVariant.value.id,
    qty: 1,
    upc: selectedVariant.value.upc!
  })
}

const updateInCart = async () => {
  loading.value = true

  try {
    if (context === 'update-cart-item') {
      const { id = '', itemId = '' } = product || {}
      const currentItem = itemId && cart.items.find(({ id }) => id === itemId)

      if (selectedVariant.value?.id)
        setGtmCartProductsMap(selectedVariant.value?.id, { ...details.value, list: gtmConfig?.list })

      if (currentItem)
        setGtmCartProductVariant(selectedVariant.value?.id!, getGTMProduct(currentItem.productId))

      await updateItemInCart(
        itemId,
        id,
        activeProduct.value,
        selectedVariant.value?.id!,
        selectedVariant.value?.upc,
        undefined,
        undefined,
        storeId
      )
    }

    if (context === 'update-saved-item')
      await updateSavedItemInCart(product!.itemId as string, selectedVariant.value?.id as string, url.value)

    await toast.add({
      props: {
        title: $t.success,
        message: replaceAll($t.cartItemEdited, { name: details.value?.name }),
        type: 'success',
      },
      promise: true
    })
  }
  catch (err) {
    log.error(err)
    delete gtmTrace.value[selectedVariant.value?.id!]
  }

  loading.value = false

  if (cart.errorMessage) {
    cart.setNotification('error', cart.errorMessage)
    if (selectedVariant.value?.id && details.value)
      removeGtmCartProductsMap(selectedVariant.value?.id, details.value.id)
  }

  if ($feature.showMiniCartOnAddProduct || closeOnAdd)
    emit('resolve')
}

const selectSize = (value: string, label: string, type: string) => {
  attributeSelection[type] = value

  const eventLabel = `${activeProduct.value} - ${label}`

  if (parentInteractionOrigin.value === 'shop-the-look')
    $gtm.push('shopTheLook.onSelectSize', eventLabel, { bundleId: gtmConfig!.bundleId! })
  else if (parentInteractionOrigin.value === 'cart')
    $gtm.push('cart.onSelectSize', eventLabel)
  else
    $gtm.push('plpPage.onSelectSize', eventLabel)

  if (enableScroll.value && nextAttribute.value) {
    scrollToElement(`size-picker-${nextAttribute.value.type}`)
    enableScroll.value = false
  }
}

const selectNewColor = (id, label) => {
  activeProduct.value = id

  const eventLabel = `${id} - ${label}`

  if (parentInteractionOrigin.value === 'shop-the-look')
    $gtm.push('shopTheLook.onSelectColor', eventLabel, { bundleId: gtmConfig!.bundleId! })
  else if (parentInteractionOrigin.value === 'cart')
    $gtm.push('cart.onSelectColor', eventLabel)
  else
    $gtm.push('plpPage.onSelectColor', eventLabel)
}

const handleNotifyMe = () => {
  const attributeSelectionLabels = useAttributeSelectionLabels(details, attributeSelection)
  const setNotification = ({ message, type }) => toast.add({
    props: {
      type,
      message
    }
  })

  const productColor = useProductColor(details)
  const size = attributeSelectionLabels.value.size?.value || attributeSelection.size
  const length = attributeSelection.length
  const width = attributeSelection.width

  PanelNotifyMe.keep().open({
    productId: selectedVariant.value?.id!,
    selectedAttributes: attributeSelectionLabels.value,
    gtmEventLabel: `${details.value?.id} - ${productColor.value?.label}: ${[size, length, width].filter(Boolean).join('/')}`,
    setNotification,
    subscriptionType: details.value?.subscriptionType,
    productName: details.value?.name || '',
  })

  $gtm.push('pdpPage.onNotifyMeOpen', activeProduct.value)
}

const signInToBuy = async () => {
  await DialogSignIn.keep().open({
    formLocation: 'modal:single:quickshop:none',
    isSignInToBuy: true
  })
  if (auth.loggedIn) {
    await until(() => cart.pending).toBe(false)
    addToCart()
  }
}

const handleAddToCart = () => {
  if (context === 'bundle-selection') saveBundleSelection()
  else if (isSignInToBuy.value) signInToBuy()
  else addToCart()
}

const preselectAttributes = async () => {
  if (!inventory.value)
    await until(inventoryPending).toBe(false)

  details.value?.attributes.forEach(({ type, options }) => {
    const availableOptions = options.filter(({ available }) => available)

    if (options?.length === 1)
      attributeSelection[type] = availableOptions[0]?.value || options[0].value
    else if ($feature.enablePreselectFirstWidth && type === 'width')
      attributeSelection[type] ??= availableOptions[0]?.value || options[0].value
  })
}

setProductDetailViewTracking(
  activeProduct,
  details,
  productPending,
  inventory,
  inventoryPending,
  productError,
  gtmConfig
)

whenever(() => !getIsLoading(activeProduct.value), () => {
  preselectAttributes()
}, { once: true })

onMounted(() => {
  cart.clearNotification()

  if (interactionOriginMap[gtmConfig?.appSource!])
    setInteractionOrigin(gtmConfig!.appSource, true)
  else
    setInteractionOrigin()

  parentInteractionOrigin.value = getInteractionOrigin()
})

onUnmounted(() => {
  if (!closeOnAdd)
    cart.clearNotification()

  if (interactionOriginMap[gtmConfig?.appSource!])
    setInteractionOrigin(parentInteractionOrigin.value || '')
})

if (!closeOnAdd) {
  watch(cart.notification, () => {
    if (cart.notification.message)
      scrollToElement('notificationSection')
  })
}
</script>
